import { DeviceDeleteTooltip } from "../../../style/contents/backup/backup";
import * as Tooltip from "@radix-ui/react-tooltip";
import type { ImageContentsContext, ImageContentItem } from "./image.contents";
import { useEffect, useState } from "react";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import { ImageFileListContainer } from "../../../style/contents/image/image";
import { useAuthStore } from "../../../atoms/user.auth.store";
import { useLocation, useParams } from "react-router-dom";
import {
  fetchBackupUserListLoad,
  fetchImageBackupList,
  fetchDiskBackupContents,
} from "../../../api/admin";
import UserInfo from "../../../components/backup/user.info";
import SearchForm from "../../../components/common/search.form";
import ImageDriveListTable from "../../../components/backup/image.drive.list.table";
import type { BackupUser } from "../backup/backup.user";
import { fetchLoadClientLogin } from "../../../api/auth";

export interface ImageBackupFileItem {
  backupId: number;
  userId: number;
  userName: string;
  userAccount: string;
  fileName: string;
  deviceId: string;
  type: "disk" | string;
  size: number;
  s3Url: string;
  backupStamp: string;
  createdDate: string | number;
  contentEntry?: boolean;
  fileId?: number;
  backupHistoryId?: number;
  rootDirectoryPath?: string | null;
  isWorm?: boolean;
}

const decodeBase64Param = (value: string | null) => {
  if (!value) return "";

  try {
    return decodeURIComponent(atob(value));
  } catch {
    return decodeURIComponent(value);
  }
};

export interface ImageDriveContext {
  userId: number;
  deviceId: string;
  userAccount: string;
  pcName: string;
}

const ImageDrive = ({
  context,
  onOpenImage,
  contentsContext,
  onFolderClick,
  canGoBack = false,
  onBack,
}: {
  context?: ImageDriveContext;
  canGoBack?: boolean;
  onBack?: () => void;
  onOpenImage?: (file: ImageBackupFileItem, directoryPath: string) => void;
  contentsContext?: ImageContentsContext;
  onFolderClick?: (path: string) => void;
}) => {
  const { role } = useAuthStore();

  const [fileList, setFileList] = useState<ImageBackupFileItem[]>([]);
  const [userData, setUserData] = useState<BackupUser[]>([]);
  const [loginToken, setLoginToken] = useState("");

  const [searchKeyword, setSearchKeyword] = useState("");
  const [pageSize, setPageSize] = useState("10");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  const adminUserId = localStorage.getItem("userId");
  const [contentRows, setContentRows] = useState<ImageBackupFileItem[]>([]);
  const [contentsLoading, setContentsLoading] = useState(false);
  const [contentsError, setContentsError] = useState(false);
  const contentUserId = contentsContext?.targetUserId;
  const contentDeviceId = contentsContext?.targetDeviceId;
  const contentStamp = contentsContext?.diskBackupStamp;
  const contentPath = contentsContext?.directoryPath;

  useEffect(() => {
    if (contentUserId == null || !contentDeviceId || !contentStamp || contentPath == null || !adminUserId) return;
    let active = true;
    setContentsLoading(true);
    setContentsError(false);
    setContentRows([]);
    fetchDiskBackupContents(adminUserId, {
      targetUserId: contentUserId, targetDeviceId: contentDeviceId,
      diskBackupStamp: contentStamp, directoryPath: contentPath,
    }).then(response => {
      if (!active) return;
      setContentRows((response.contents || []).map((item: ImageContentItem) => ({
        backupId: item.id, userId: contentUserId, deviceId: contentDeviceId,
        userName: "", userAccount: "", fileName: item.name, type: item.type,
        size: item.size, s3Url: item.s3Url, backupStamp: contentStamp,
        createdDate: item.createdDate, rootDirectoryPath: item.path, contentEntry: true,
        fileId: item.fileId, backupHistoryId: item.backupHistoryId, isWorm: item.isWorm,
      })));
    }).catch(() => { if (active) setContentsError(true); })
      .finally(() => { if (active) setContentsLoading(false); });
    return () => { active = false; };
  }, [contentUserId, contentDeviceId, contentStamp, contentPath, adminUserId, retry]);

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const { path: rawDeviceId = "" } = useParams<{ path: string }>();
  const deviceId = context?.deviceId ?? decodeURIComponent(rawDeviceId);
  const id = context ? String(context.userId) : queryParams.get("id");
  const userAccount = context?.userAccount ?? queryParams.get("userAccount");
  const pcName =
    context?.pcName ?? decodeBase64Param(queryParams.get("pcName"));
  const rawDirectoryInfo = localStorage.getItem("directory");
  const directoryInfo = rawDirectoryInfo ? JSON.parse(rawDirectoryInfo) : null;
  const filteredFileList = fileList.filter(
    (file) => file.userId === Number(id) && file.deviceId === deviceId,
  );

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    // TODO: fetchData(keyword, pageSize);
  };

  const handlePageSizeChange = (size: string) => {
    setPageSize(size);
  };

  useEffect(() => {
    if (role !== "admin") return;

    const loadUserList = async () => {
      const userId = localStorage.getItem("userId");
      const companyCode = localStorage.getItem("companyCode");

      try {
        const response = await fetchBackupUserListLoad(
          Number(userId),
          companyCode || "",
        );
        setUserData(response.userList);
      } catch (err) {
        console.error("사용자 목록 로딩 실패:", err);
      }
    };

    loadUserList();
  }, [role]);

  useEffect(() => {
    if (role !== "admin" || userData.length === 0 || !userAccount || !id) {
      return;
    }

    const loadUserLogin = async () => {
      const companyCode = localStorage.getItem("companyCode");
      const matchedUser = userData.find(
        (user) => user.userAccount === userAccount,
      );

      if (!matchedUser) {
        console.warn("해당 userAccount에 대한 유저 정보를 찾을 수 없습니다.");
        setLoginToken("");
        return;
      }

      try {
        const response = await fetchLoadClientLogin({
          companyCode: companyCode || "",
          userAccount: matchedUser.userAccount,
          password: matchedUser.password,
          deviceId: "",
        });

        setLoginToken(response.jwtToken);
      } catch (err) {
        console.error("로그인 실패:", err);
        setLoginToken("");
      }
    };

    loadUserLogin();
  }, [id, role, userAccount, userData]);

  useEffect(() => {
    const loadFileList = async () => {
      if (!deviceId || !id || !adminUserId) return;

      setIsLoading(true);
      setLoadError(false);
      try {
        const response = await fetchImageBackupList(adminUserId);
        setFileList(response.backupList || []);
      } catch (err) {
        setLoadError(true);
        console.error("파일 목록 조회 실패:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (role === "admin") loadFileList();
    else setIsLoading(false);
  }, [adminUserId, deviceId, id, role, retry]);
  return (
    <ImageFileListContainer>
      {!context && (
        <>
          <ContentsTopHeader
            title="디바이스 목록  > 드라이브 이미지 백업 목록"
            backArrowPath="-1"
          />
          <div className="contents-top">
            <div className="device-name">
              <p>
                {pcName}({deviceId})
              </p>
            </div>
            <div className="device-info">
              <p>마지막 백업시간 : {directoryInfo?.createDate || "-"}</p>
              {role === "admin" && <UserInfo userAccount={userAccount || ""} />}
            </div>
          </div>
        </>
      )}
      <div className="file-list-container">
        <div className="top">
          <div className="left">
            {onBack && (
              <Tooltip.Provider delayDuration={200}>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <span
                      className="back-trigger"
                      tabIndex={canGoBack ? undefined : 0}
                    >
                      <button
                        type="button"
                        aria-label="이전 폴더로 돌아가기"
                        className="folder-back-button"
                        disabled={!canGoBack}
                        onClick={onBack}
                      >
                        <img src="/assets/left-arrow.png" alt="" />
                      </button>
                    </span>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <DeviceDeleteTooltip side="bottom" sideOffset={6}>
                      뒤로가기
                      <Tooltip.Arrow fill="#333333" />
                    </DeviceDeleteTooltip>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            )}
            <span>{pcName + `(` + deviceId + `)`}</span>
          </div>
          <div className="right">
            <SearchForm
              onSearch={handleSearch}
              onPageSizeChange={handlePageSizeChange}
              pageSize={pageSize}
              pageSizeHidden={!!context}
            />
          </div>
        </div>
        {(contentsContext ? contentsError : loadError) && (
          <div role="alert">백업 목록을 불러오지 못했습니다. <button type="button" onClick={() => setRetry(value => value + 1)}>다시 시도</button></div>
        )}
          <ImageDriveListTable
            onOpenImage={onOpenImage}
            onFolderClick={onFolderClick}
            data={contentsContext ? contentRows : filteredFileList}
            searchKeyword={searchKeyword}
            pageSize={parseInt(pageSize)}
            paginationEnabled={!context}
            navigationSearch={
              context
                ? new URLSearchParams({
                    id: String(context.userId),
                    userAccount: context.userAccount,
                    pcName: btoa(encodeURIComponent(context.pcName || "")),
                  }).toString()
                : undefined
            }
            isLoading={contentsContext ? contentsLoading : isLoading}
            userId={id || ""}
            loginToken={loginToken}
          />

      </div>
    </ImageFileListContainer>
  );
};

export default ImageDrive;
